import AlertDialog from "components/AlertDialog";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useActionWithPath } from "utils/query";
import { AGENT_COMMAND_PATH } from "utils/apiPaths";
import { CLUSTER_ACTION_TYPE } from "constants/common";

const ResourceTypes = [
  { name: "Conductor Server", value: "CONDUCTOR" },
  { name: "Conductor Workers", value: "WORKERS" },
];

const MemoryUnitOptions = [
  { name: "MiB", value: "MiB" },
  { name: "GiB", value: "GiB" },
];

export default function UpdateDeploymentMemoryDialog({
  clusterName,
  open,
  closeDialog,
  showError,
  showSuccess,
  refetchLogs,
  initialResource,
}: {
  clusterName?: string;
  open: boolean;
  closeDialog: () => void;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
  refetchLogs: () => void;
  initialResource?: "CONDUCTOR" | "WORKERS";
}) {
  const navigate = useNavigate();

  const formSchema = yup.object().shape({
    memory: yup
      .number()
      .required("Memory is required")
      .integer("Memory must be an integer")
      .when("unit", (unit: any, schema: any) =>
        unit === "GiB"
          ? schema.min(1, "Memory must be at least 1 GiB")
          : schema.min(256, "Memory must be at least 256 MiB")
      )
      .typeError("Memory is required"),
    unit: yup
      .string()
      .oneOf(["MiB", "GiB"], "Invalid unit")
      .required("Unit is required"),
    resource: yup
      .string()
      .required("Resource type is required")
      .typeError("Resource type is required"),
  });

  const defaultValues = useMemo(
    () =>
      ({
        memory: "",
        unit: MemoryUnitOptions[0].value,
        resource:
          initialResource ??
          (ResourceTypes[0].value as "CONDUCTOR" | "WORKERS"),
      } as const),
    [initialResource]
  );

  const {
    control,
    formState: { isValid },
    handleSubmit,
    reset,
    trigger,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset(defaultValues);
    }
  }, [open, reset, defaultValues]);

  const updateMemoryAction = useActionWithPath({
    onSuccess: (data: any) => {
      if (data?.status === "ERROR") {
        showError("Update deployment memory failed");
      } else {
        showSuccess(
          `Update deployment memory has been scheduled successfully.`
        );
        if (data.id) {
          navigate(`/home/clusterDetails/${clusterName}/history`);
        }
      }
      refetchLogs();
    },
    onError: async (response: Response) => {
      try {
        const json = await response.json();
        showError(`Update deployment memory failed: ${json.message}`);
      } catch {
        showError("Update deployment memory failed");
      }
      refetchLogs();
    },
  });

  const onSubmit = (data: {
    resource: string;
    memory: string;
    unit: "MiB" | "GiB";
  }) => {
    closeDialog();
    const memoryMi =
      data.unit === "GiB" ? String(Number(data.memory) * 1024) : data.memory;
    // @ts-ignore
    updateMemoryAction.mutate({
      method: "POST",
      path: AGENT_COMMAND_PATH,
      body: JSON.stringify({
        clusterName,
        command: CLUSTER_ACTION_TYPE.UPDATE_DEPLOYMENT_MEMORY,
        parameters: {
          resource: data.resource,
          memoryMi,
        },
      }),
    });
  };

  return (
    <AlertDialog
      textTitle={"Update Deployment Memory"}
      textContent=""
      open={open}
      setOpen={() => closeDialog()}
      isConfirmDisabled={!isValid}
      handleConfirm={() => {
        // @ts-ignore
        handleSubmit(onSubmit)();
      }}
    >
      <Typography>
        Update memory for a deployment in cluster <strong>{clusterName}</strong>
        :
      </Typography>
      <form>
        <Box sx={{ minWidth: 120, marginTop: "16px", display: "flex" }}>
          <Box style={{ width: "100%" }}>
            <FormControl fullWidth>
              <InputLabel id="resource-type-label">Resource Type</InputLabel>
              <Controller
                control={control}
                name="resource"
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="resource-type-label"
                    id="resource-type-select"
                    required
                    label="Resource Type"
                    onChange={(event: any) => field.onChange(event)}
                  >
                    {ResourceTypes.map((x: any) => (
                      <MenuItem key={x.value} value={x.value}>
                        {x.name}
                      </MenuItem>
                    ))}
                  </Select>
                )}
                rules={{ required: true }}
              />
            </FormControl>
            <Box
              style={{
                display: "flex",
                gap: 12,
                marginTop: 16,
                marginBottom: 16,
              }}
            >
              <FormControl style={{ flex: 1 }} fullWidth>
                <Controller
                  control={control}
                  name="memory"
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      required
                      label="Memory"
                      placeholder="e.g. 2048"
                      error={!!fieldState.error && fieldState.isTouched}
                      helperText={
                        fieldState.isTouched ? fieldState.error?.message : ""
                      }
                      onChange={(event) => {
                        const { value } = event.target;
                        const regex = /^[0-9\b]+$/;

                        if (value === "" || regex.test(value)) {
                          field.onChange(event);
                        }
                      }}
                    />
                  )}
                  rules={{ required: true }}
                />
              </FormControl>
              <FormControl style={{ width: 140 }} fullWidth>
                <InputLabel id="memory-unit-label">Unit</InputLabel>
                <Controller
                  control={control}
                  name="unit"
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="memory-unit-label"
                      id="memory-unit-select"
                      required
                      label="Unit"
                      onChange={(event: any) => {
                        field.onChange(event);
                        trigger("memory");
                      }}
                    >
                      {MemoryUnitOptions.map((x: any) => (
                        <MenuItem key={x.value} value={x.value}>
                          {x.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                  rules={{ required: true }}
                />
              </FormControl>
            </Box>
          </Box>
        </Box>
      </form>
    </AlertDialog>
  );
}
